import { Column, Index, PrimaryGeneratedColumn } from 'typeorm';
import { Entity } from '../../decorators';

/**
 * Daily trend rows behind the Compliance Dashboard's 30-day sparkline: one row
 * per snapshot_date carrying the active-buyer / verified counts
 * (grade_total_clients / grade_matched_clients). Written by
 * ComplianceOverviewService.refresh on every refresh trigger and pruned past
 * 90 days.
 *
 * The remaining KPI columns (pending_match … health_score) fed the tile
 * dashboard that PD-15874 retired; nothing writes or reads them any more. They
 * stay until a cleanup migration drops them, and rows with snapshot_date = NULL
 * are leftovers from the old "current" refresh.
 */
@Index('idx_snapshot_date', ['snapshotDate'], { unique: true })
@Index('idx_computed_at', ['computedAt'])
@Entity('license_compliance_snapshot', {}, { auditable: false })
export class LicenseComplianceSnapshotEntity {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	public id!: number;

	@Column('date', { name: 'snapshot_date', nullable: true })
	public snapshotDate!: string | null;

	@Column('int', { name: 'pending_match', default: 0 })
	public pendingMatch!: number;

	@Column('int', { name: 'expiring_soon', default: 0 })
	public expiringSoon!: number;

	@Column('int', { name: 'expired', default: 0 })
	public expired!: number;

	@Column('int', { name: 'at_risk', default: 0 })
	public atRisk!: number;

	@Column('int', { name: 'recently_changed', default: 0 })
	public recentlyChanged!: number;

	@Column('int', { name: 'total_licenses', default: 0 })
	public totalLicenses!: number;

	@Column('int', { name: 'matched_to_registry', default: 0 })
	public matchedToRegistry!: number;

	/**
	 * Bad Standing KPI (PD-13949): customer licenses matched to a Cancelled/
	 * Revoked/Suspended registry license. Nullable so snapshots written before
	 * this column existed read as "not available" and the dashboard falls back
	 * to a live count (same transition pattern as the grade_* columns).
	 */
	@Column('int', { name: 'bad_standing', nullable: true })
	public badStanding!: number | null;

	/**
	 * PD-13929: the ACTIVE-BUYERS slice of bad_standing — adverse-matched
	 * customer licenses whose owning client has sales in the last 90 days (the
	 * same demand window the health grade uses). This is the inherently
	 * actionable subset: an adverse license on a client who still buys from us
	 * warrants review; a dormant account's does not. Nullable for the same
	 * pre-column transition pattern as bad_standing.
	 */
	@Column('int', { name: 'bad_standing_active_buyers', nullable: true })
	public badStandingActiveBuyers!: number | null;

	/**
	 * Pending work-queue counts, snapshot-backed so the dashboard stops running
	 * the two heaviest live queries on every page load (an uncached aggregate
	 * over client_license_match and an uncached COUNT over client_license with
	 * two correlated NOT EXISTS). Freshness is bounded by the existing refresh
	 * triggers (15-minute scheduler, post-FULL_SCAN inline refresh, debounced
	 * refresh after match-review mutations, manual dashboard refresh).
	 * Nullable so snapshots written before these columns existed read as "not
	 * available" and the dashboard falls back to a live count (same transition
	 * pattern as bad_standing / grade_*).
	 *
	 * pendingConfirmation = DISTINCT clients with a pending_review match row
	 * (suppression + closed-client exclusions applied — mirrors getMatchStats).
	 * pendingManualMatch = licenses with no registry link and no pending
	 * suggestion, excluding closed-lifecycle clients (mirrors the
	 * `under_review` list facet).
	 */
	@Column('int', { name: 'pending_confirmation', nullable: true })
	public pendingConfirmation!: number | null;

	@Column('int', { name: 'pending_manual_match', nullable: true })
	public pendingManualMatch!: number | null;

	@Column('char', { name: 'health_score', length: 1, nullable: true })
	public healthScore!: string | null;

	/**
	 * Grade inputs (PD-14395): the demand-based client-coverage counts the
	 * letter grade was computed from — denominator = active Customer clients
	 * with a sale in the last 90 days, numerator = those with >=1 matched,
	 * non-expired license. Persisted so the dashboard can display the NUMERIC
	 * score from the SAME formula as the letter (the ring previously showed a
	 * license-row match % next to a client-coverage letter). Nullable so
	 * snapshots written before this column existed read as "not available"
	 * and the dashboard falls back to computing the counts live.
	 */
	@Column('int', { name: 'grade_total_clients', nullable: true })
	public gradeTotalClients!: number | null;

	@Column('int', { name: 'grade_matched_clients', nullable: true })
	public gradeMatchedClients!: number | null;

	@Column('datetime', {
		name: 'computed_at',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public computedAt!: Date;

	@Column('datetime', {
		name: '_created_date',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public createdDate!: Date;
}
